import { X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useMatchSettingsStore } from "@/store/MatchSettingsStore";
import { cn } from "@/lib/utils";

interface MatchSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MatchSettingsDrawer({ isOpen, onClose }: MatchSettingsDrawerProps) {
  const {
    priorityPreference,
    routePreference,
    cargoPreference,
    contextTemplate,
    responseStyle,
    setPriorityPreference,
    setRoutePreference,
    setCargoPreference,
    setContextTemplate,
    setResponseStyle,
  } = useMatchSettingsStore();

  return (
    <div
      className={cn(
        "h-screen w-full bg-background shadow-xl transition-all duration-300 ease-in-out flex flex-col",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
    >
      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-background z-10">
        <h2 className="text-lg font-semibold">匹配设置</h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* 内容 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 pb-20">
        {/* 接单偏好设置 */}
        <div>
          <h3 className="text-base font-semibold mb-3">接单偏好设置</h3>
          <div className="space-y-2">
            <button
              onClick={() => setPriorityPreference("system")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                priorityPreference === "system"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              系统智能推荐
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPriorityPreference("distance")}
                className={cn(
                  "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                  priorityPreference === "distance"
                    ? "bg-blue-50 text-blue-600 border border-blue-200"
                    : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                )}
              >
                距离优先
              </button>
              <button
                onClick={() => setPriorityPreference("amount")}
                className={cn(
                  "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                  priorityPreference === "amount"
                    ? "bg-blue-50 text-blue-600 border border-blue-200"
                    : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
                )}
              >
                金额优先
              </button>
            </div>
          </div>
        </div>

        {/* 路途设置 */}
        <div>
          <h3 className="text-base font-semibold mb-3">路途设置</h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setRoutePreference("short")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                routePreference === "short"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              短途优先
            </button>
            <button
              onClick={() => setRoutePreference("long")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                routePreference === "long"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              长途优先
            </button>
          </div>
        </div>

        {/* 货源设置 */}
        <div>
          <h3 className="text-base font-semibold mb-3">货源设置</h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setCargoPreference("bulk")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                cargoPreference === "bulk"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              大宗优先
            </button>
            <button
              onClick={() => setCargoPreference("general")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                cargoPreference === "general"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              普货优先
            </button>
          </div>
        </div>

        {/* 上下文设置 */}
        <div>
          <h3 className="text-base font-semibold mb-3">上下文设置</h3>
          <div className="space-y-2">
            <button
              onClick={() => setContextTemplate("none")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                contextTemplate === "none"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              不使用上下文
            </button>
            <button
              onClick={() => setContextTemplate("medium")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                contextTemplate === "medium"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              中等上下文
            </button>
            <button
              onClick={() => setContextTemplate("max")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200",
                contextTemplate === "max"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              最大上下文
            </button>
          </div>
        </div>

        {/* 模型设置 */}
        <div>
          <h3 className="text-base font-semibold mb-3">模型设置</h3>
          <div className="space-y-2">
            <button
              onClick={() => setResponseStyle("concise")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 text-left",
                responseStyle === "concise"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              <div className="font-medium mb-1">精准回复</div>
              <div className="text-xs opacity-70">直接给出结论和关键信息，简洁明了</div>
            </button>
            <button
              onClick={() => setResponseStyle("detailed")}
              className={cn(
                "w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 text-left",
                responseStyle === "detailed"
                  ? "bg-blue-50 text-blue-600 border border-blue-200"
                  : "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100"
              )}
            >
              <div className="font-medium mb-1">发散思维</div>
              <div className="text-xs opacity-70">提供完整的分析和说明，详尽全面</div>
            </button>
          </div>
        </div>

        {/* 提示信息 */}
        <div className="pt-4 border-t">
          <p className="text-xs text-gray-500">
            以上设置，决定系统订单推荐偏好
          </p>
        </div>
      </div>
    </div>
  );
}

export default MatchSettingsDrawer;
